<template>
  <v-container fluid>
    <back-link-component :to="{ name: 'WorkRecords' }" :label="$tt('DocFormTabNameTaskRecordList')" />
    <h2>{{ $tt('feed.spatialEdit') }}</h2>
    <v-layout align-center class="ml-2 mb-2">
      <span class="caption grey--text mr-4">{{ $tt('feed.trajectories') }}: {{ taskIds.length }}</span>
      <v-btn small color="primary" class="mr-2" :disabled="isDrawing || !routes.length" @click="startClipDraw()">
        {{ $tt('feed.clipDraw') }}
      </v-btn>
      <v-btn
        small
        color="primary"
        class="mr-2"
        :disabled="isDrawing || !routes.length || !storeFields.length"
        @click="fieldPicker = true"
      >
        {{ $tt('feed.clipByField') }}
      </v-btn>
      <v-btn
        small
        color="primary"
        class="mr-2"
        :disabled="isDrawing || !routes.length"
        @click="openSaveDialog('field')"
      >
        {{ $tt('feed.saveAsField') }}
      </v-btn>
      <v-spacer />
      <v-btn small text to="/works">{{ $tt('customCancel') }}</v-btn>
    </v-layout>

    <!-- 編集対象の軌跡だけを表示する集中マップ (メインマップはビューアー専任) -->
    <gmap-map
      ref="googleMapRef"
      :center="getDefaultMapCenter()"
      :zoom="getDefaultZoomLevel()"
      map-type-id="roadmap"
      :options="{ streetViewControl: false, rotateControl: false, fullscreenControl: false }"
      style="width: 100%; height: calc(100vh - 190px); position: relative;"
    >
      <gmap-polyline
        v-for="r in routes"
        :key="'er-' + r.id"
        :path="r.path"
        :options="{ strokeColor: '#FF9800', strokeWeight: 5, strokeOpacity: 0.9, clickable: false, zIndex: 2 }"
      />
      <div class="finish-btn on-map-btn" v-if="isDrawing && vertexCount >= 3" @click="finishClipDraw()">
        <v-icon color="primary">mdi-check-bold</v-icon>
      </div>
      <div class="draw-hint" v-if="isDrawing">{{ $tt('customFieldDrawHint') }}</div>
    </gmap-map>

    <!-- 圃場でクリップ: 圃場選択 (シーズン圃場: 名称は圃場名+年を初期値にする) -->
    <v-dialog v-model="fieldPicker" max-width="360">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.clipByField') }}</v-card-title>
        <v-list dense>
          <v-list-item v-for="f in storeFields" :key="f.pk" @click="clipByField(f)">
            <v-list-item-title>{{ f.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>

    <!-- 保存ダイアログ (クリップ → work_records、圃場化 → createField) -->
    <v-dialog v-model="saveDialog.show" max-width="360">
      <v-card>
        <v-card-title class="subtitle-1">
          {{ saveDialog.kind === 'field' ? $tt('feed.saveAsField') : $tt('feed.clipDraw') }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="saveDialog.name"
            dense
            :label="saveDialog.kind === 'field' ? $tt('customFieldCreateWithMapFieldName') : $tt('feed.workName')"
          />
          <v-text-field
            v-if="saveDialog.kind === 'field'"
            v-model.number="saveDialog.width"
            dense
            type="number"
            min="0.5"
            step="0.5"
            :label="$tt('feed.workWidthM')"
          />
          <!-- シーズン圃場: 有効期間 (作付け期間) と作目。空欄は無期限の恒久圃場 -->
          <v-row dense v-if="saveDialog.kind === 'field'">
            <v-col cols="6">
              <v-text-field
                v-model="saveDialog.validFrom"
                dense
                type="date"
                clearable
                :label="$tt('feed.fieldValidFrom')"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="saveDialog.validTo"
                dense
                type="date"
                clearable
                :label="$tt('feed.fieldValidTo')"
              />
            </v-col>
          </v-row>
          <v-text-field
            v-if="saveDialog.kind === 'field'"
            v-model="saveDialog.crop"
            dense
            :label="$tt('feed.mapSetCrop')"
          />
          <v-textarea
            v-if="saveDialog.kind !== 'field'"
            v-model="saveDialog.memo"
            dense
            rows="2"
            :label="$tt('feed.recordMemo')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelSaveDialog()">{{ $tt('customCancel') }}</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!saveDialog.name || (saveDialog.kind === 'field' && !(saveDialog.width > 0))"
            @click="saveDialog.kind === 'field' ? saveField() : saveWork()"
          >
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import GoogleMapMixin from '@/components/mixins/google-map-mixin'
import feedApiClient from '@/lib/feed-api-client'
import Toast from '@/lib/toast'
import * as turf from '@turf/turf'

export default {
  name: 'WorkEditor',
  mixins: [GoogleMapMixin],
  data() {
    return {
      routes: [],
      isDrawing: false,
      vertexCount: 0,
      drawPolygon: null,
      mapClickListener: null,
      clipPolygonCoords: null,
      fieldPicker: false,
      saveDialog: { show: false, kind: 'clip', name: '', memo: '', width: 3, validFrom: '', validTo: '', crop: '' },
      saving: false,
    }
  },
  computed: {
    taskIds() {
      const q = this.$route.query.tasks
      return q
        ? String(q)
            .split(',')
            .filter(Boolean)
        : []
    },
    storeFields() {
      return this.$store.getters.fields
    },
  },
  mounted() {
    this.loadRoutes()
  },
  beforeDestroy() {
    this.clearDrawing()
  },
  methods: {
    async loadRoutes() {
      for (const id of this.taskIds) {
        const sep = id.indexOf('-')
        if (sep < 0) continue
        try {
          const r = await feedApiClient.getRoute(id.slice(0, sep), id.slice(sep + 1))
          const pts = (r.data.route || []).map(p => ({ lat: p[0], lng: p[1] }))
          if (pts.length > 1) {
            this.routes.push({ id, path: pts })
          }
        } catch (e) {
          // 経路なしはスキップ
        }
      }
      this.fitToRoutes()
    },
    fitToRoutes() {
      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.setTilt(0)
        const bounds = new google.maps.LatLngBounds()
        let has = false
        for (const r of this.routes) {
          for (const p of r.path) {
            has = true
            bounds.extend(p)
          }
        }
        if (has) {
          map.fitBounds(bounds)
        }
      })
    },
    clearDrawing() {
      this.isDrawing = false
      this.vertexCount = 0
      this.clipPolygonCoords = null
      if (this.mapClickListener) {
        this.mapClickListener.remove()
        this.mapClickListener = null
      }
      if (this.drawPolygon) {
        this.drawPolygon.setMap(null)
        this.drawPolygon = null
      }
    },
    // クリップ範囲のクリック描画 (FieldCreate と同じ方式、DrawingManager 非依存)
    startClipDraw() {
      this.clearDrawing()
      this.isDrawing = true
      this.$refs.googleMapRef.$mapPromise.then(map => {
        this.mapClickListener = map.addListener('click', e => {
          if (!this.drawPolygon) {
            this.drawPolygon = new google.maps.Polygon({
              map,
              paths: [e.latLng],
              fillColor: '#FF9800',
              fillOpacity: 0.15,
              strokeColor: '#FF9800',
              strokeWeight: 2,
              clickable: false,
              editable: true,
              zIndex: 4,
            })
          } else {
            this.drawPolygon.getPath().push(e.latLng)
          }
          this.vertexCount = this.drawPolygon.getPath().getLength()
        })
      })
    },
    finishClipDraw() {
      if (!this.drawPolygon || this.drawPolygon.getPath().getLength() < 3) return
      this.isDrawing = false
      if (this.mapClickListener) {
        this.mapClickListener.remove()
        this.mapClickListener = null
      }
      const ring = this.drawPolygon
        .getPath()
        .getArray()
        .map(p => [p.toJSON().lng, p.toJSON().lat])
      ring.push(ring[0])
      this.clipPolygonCoords = [ring]
      this.openSaveDialog('clip')
    },
    clipByField(f) {
      this.fieldPicker = false
      let coords = null
      try {
        const g = JSON.parse(f.geoJson)
        const feature = g.features && g.features[0]
        if (feature && feature.geometry && feature.geometry.type === 'Polygon') {
          coords = feature.geometry.coordinates
        }
      } catch (e) {
        console.error(e)
      }
      if (!coords) return
      this.clearDrawing()
      this.clipPolygonCoords = coords
      // シーズン圃場を意識した初期名称 (例: 「田んぼ北側 2026」)
      this.openSaveDialog('clip', f.name + ' ' + new Date().getFullYear())
    },
    openSaveDialog(kind, name) {
      this.saveDialog = { show: true, kind, name: name || '', memo: '', width: 3 }
    },
    cancelSaveDialog() {
      this.saveDialog.show = false
      if (this.saveDialog.kind === 'clip') {
        this.clearDrawing()
      }
    },
    buildSources() {
      return this.taskIds.map(id => {
        const sep = id.indexOf('-')
        return { session_id: id.slice(0, sep), task_group_id: id.slice(sep + 1) }
      })
    },
    haversine(a, b) {
      const R = 6371000
      const dLat = ((b.lat - a.lat) * Math.PI) / 180
      const dLng = ((b.lng - a.lng) * Math.PI) / 180
      const s =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
      return 2 * R * Math.asin(Math.sqrt(s))
    },
    pathStats(paths) {
      let dist = 0
      const bbox = [Infinity, Infinity, -Infinity, -Infinity]
      for (const path of paths) {
        for (let i = 0; i < path.length; i++) {
          const p = path[i]
          if (i > 0) dist += this.haversine(path[i - 1], p)
          bbox[0] = Math.min(bbox[0], p.lng)
          bbox[1] = Math.min(bbox[1], p.lat)
          bbox[2] = Math.max(bbox[2], p.lng)
          bbox[3] = Math.max(bbox[3], p.lat)
        }
      }
      return { distance_m: Math.round(dist), bbox: dist > 0 ? bbox : null }
    },
    clipPaths(paths, polygonCoords) {
      // ポリゴン内の連続区間ごとに分割 (外に出た区間を結ばない)
      const poly = turf.polygon(polygonCoords)
      const out = []
      for (const path of paths) {
        let seg = []
        for (const p of path) {
          if (turf.booleanPointInPolygon(turf.point([p.lng, p.lat]), poly)) {
            seg.push(p)
          } else if (seg.length > 1) {
            out.push(seg)
            seg = []
          } else {
            seg = []
          }
        }
        if (seg.length > 1) out.push(seg)
      }
      return out
    },
    async saveWork() {
      if (!this.clipPolygonCoords) return
      const paths = this.clipPaths(this.routes.map(r => r.path), this.clipPolygonCoords)
      this.saving = true
      try {
        await feedApiClient.createWorkRecord({
          kind: 'clip',
          sources: this.buildSources(),
          params: { clip_polygon: { type: 'Polygon', coordinates: this.clipPolygonCoords } },
          name: this.saveDialog.name,
          memo: this.saveDialog.memo,
          operation_type: this.$route.query.op || null,
          started_at: this.$route.query.start || null,
          cache: this.pathStats(paths),
        })
        Toast.i18nSuccess('feed.workSaved')
        this.saveDialog.show = false
        this.$router.push('/works')
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.loadError')
      } finally {
        this.saving = false
      }
    },
    // 作付けエリア = swath union (turf.buffer + union)。作業幅は必須 (設計 65 §7.6)
    async saveField() {
      const half = this.saveDialog.width / 2
      let merged = null
      try {
        for (const r of this.routes) {
          if (r.path.length < 2) continue
          const line = turf.lineString(r.path.map(p => [p.lng, p.lat]))
          const buf = turf.buffer(line, half, { units: 'meters' })
          merged = merged ? turf.union(merged, buf) : buf
        }
      } catch (e) {
        console.error(e)
      }
      if (!merged) return
      let coordinates = merged.geometry.coordinates
      if (merged.geometry.type === 'MultiPolygon') {
        // 最大面積のポリゴンを採用 (飛び地は捨てる)
        coordinates = coordinates.slice().sort((a, b) => turf.area(turf.polygon(b)) - turf.area(turf.polygon(a)))[0]
      }
      // 外周リングを間引いて頂点数を抑える
      const simplified = turf.simplify(turf.polygon(coordinates), { tolerance: 0.00001, highQuality: true })
      const request = {
        features: [
          {
            type: 'Feature',
            geometry: simplified.geometry,
            properties: { Name: this.saveDialog.name },
          },
        ],
        propertyAsName: 'Name',
      }
      this.saving = true
      try {
        const prevPks = new Set(this.storeFields.map(f => String(f.pk)))
        await this.$store.dispatch('createField', { request, fieldName: this.saveDialog.name })
        // 有効期間・作目が入力されていれば、同期完了後に新しい圃場へ属性を保存する
        const attrs = {
          valid_from: this.saveDialog.validFrom || null,
          valid_to: this.saveDialog.validTo || null,
          crop: this.saveDialog.crop || null,
        }
        if (attrs.valid_from || attrs.valid_to || attrs.crop) {
          this.saveFieldAttrsWhenSynced(this.saveDialog.name, prevPks, attrs)
        }
        this.saveDialog.show = false
      } finally {
        this.saving = false
      }
    },
    // createField は非同期同期のため新圃場の pk を返さない。ストアの圃場一覧に
    // 新しい pk が現れるのを待って属性を保存する (見つからなければ諦める)
    saveFieldAttrsWhenSynced(fieldName, prevPks, attrs, attempt = 0) {
      const found = this.storeFields.find(f => !prevPks.has(String(f.pk)) && f.name === fieldName)
      if (found) {
        feedApiClient.putFieldAttrs(String(found.pk), attrs).catch(e => console.error(e.response || e))
        return
      }
      if (attempt < 30) {
        setTimeout(() => this.saveFieldAttrsWhenSynced(fieldName, prevPks, attrs, attempt + 1), 2000)
      }
    },
  },
}
</script>
